<template>
  <div class="step-content">
    <h5 class="mb-3">Dados Básicos</h5>

    <!-- Tipo de Credenciamento -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Tipo de Credenciamento" label-class="required">
          <b-form-radio-group
            v-model="formData.credentialing_type"
            :options="credentialingTypeOptions"
            :state="getFieldState('credentialing_type')"
            :class="getRadioGroupClass()"
            :disabled="disabled"
            @input="updateField('credentialing_type', $event)"
            stacked
          />
          <b-form-invalid-feedback>
            Selecione o tipo de credenciamento
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Área de Atuação -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Área de Atuação" label-class="required">
          <b-form-radio-group
            v-model="formData.activity_area"
            :options="activityAreaOptions"
            :state="getFieldState('activity_area')"
            :class="getRadioGroupClass()"
            :disabled="disabled"
            @input="updateField('activity_area', $event)"
            stacked
          />
          <b-form-invalid-feedback>
            Selecione uma área de atuação
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Nome da Entidade -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Nome da Entidade" label-class="required" label-for="entityName">
          <b-form-input
            id="entityName"
            :value="formData.entity_name"
            placeholder="Digite o nome da entidade"
            :state="getFieldState('entity_name')"
            :disabled="disabled"
            class="required"
            @input="updateField('entity_name', $event)"
            readonly
          />
          <b-form-invalid-feedback>
            Nome da entidade é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- CNPJ e CNAE -->
    <b-row>
      <b-col md="6">
        <b-form-group label="CNPJ" label-class="required" label-for="cnpj">
          <b-form-input
            id="cnpj"
            :value="formData.cnpj"
            v-mask="'##.###.###/####-##'"
            placeholder="00.000.000/0000-00"
            :state="getFieldState('cnpj')"
            :disabled="disabled"
            class="required"
            @input="updateField('cnpj', $event)"
            readonly
          />
          <b-form-invalid-feedback>
            CNPJ válido é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="CNAE Principal" label-class="required" label-for="mainCnae">
          <b-form-input
            id="mainCnae"
            :value="formData.main_cnae"
            placeholder="Digite o código CNAE principal da entidade"
            :state="getFieldState('main_cnae')"
            :disabled="disabled"
            class="required"
            @input="updateField('main_cnae', $event)"
          />
          <small class="form-text text-muted">
            <i class="feather icon-info mr-1"></i>
            Código da Classificação Nacional de Atividades Econômicas
          </small>
          <b-form-invalid-feedback>
            CNAE Principal é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Telefone -->
    <b-row>
      <b-col md="6">
        <b-form-group label="Telefone" label-class="required" label-for="phone">
          <b-form-input
            id="phone"
            :value="formData.phone"
            v-mask="'(##) #####-####'"
            placeholder="(00) 00000-0000"
            :state="getFieldState('phone')"
            :disabled="disabled"
            class="required"
            @input="updateField('phone', $event)"
          />
          <b-form-invalid-feedback>
            Telefone é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Endereço Completo -->
    <b-row>
      <b-col md="12">
        <b-form-group label="Endereço Completo" label-class="required" label-for="address">
          <b-form-textarea
            id="address"
            :value="formData.address"
            placeholder="Digite o endereço completo da entidade"
            rows="3"
            :state="getFieldState('address')"
            :disabled="disabled"
            class="required"
            @input="updateField('address', $event)"
          />
          <b-form-invalid-feedback>
            Endereço completo é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- E-mail -->
    <b-row>
      <b-col md="12">
        <b-form-group label="E-mail" label-class="required" label-for="email">
          <b-form-input
            id="email"
            :value="formData.email"
            type="email"
            placeholder="email@entidade.com"
            :state="getFieldState('email')"
            :disabled="true"
            readonly
            class="bg-light required"
            @input="updateField('email', $event)"
          />
          <small class="form-text text-muted">
            <i class="feather icon-lock mr-1"></i>
            Este e-mail não pode ser alterado pois é usado para identificação (juntamente com o código de segurança) e recuperação dos dados do credenciamento.
          </small>
          <b-form-invalid-feedback>
            E-mail válido é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Dados do Representante Legal -->
    <h6 class="mt-4 mb-3 text-muted">Representante Legal</h6>

    <b-row>
      <b-col md="8">
        <b-form-group label="Nome do Representante Legal" label-class="required" label-for="legalRepresentativeName">
          <b-form-input
            id="legalRepresentativeName"
            :value="formData.legal_representative_name"
            placeholder="Digite o nome completo do representante legal"
            :state="getFieldState('legal_representative_name')"
            :disabled="disabled"
            class="required"
            @input="updateField('legal_representative_name', $event)"
          />
          <b-form-invalid-feedback>
            Nome do representante legal é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col md="4">
        <b-form-group label="CPF do Representante Legal" label-class="required" label-for="legalRepresentativeCpf">
          <b-form-input
            id="legalRepresentativeCpf"
            :value="formData.legal_representative_cpf"
            v-mask="'###.###.###-##'"
            placeholder="000.000.000-00"
            :state="getFieldState('legal_representative_cpf')"
            :disabled="disabled"
            class="required"
            @input="updateField('legal_representative_cpf', $event)"
          />
          <b-form-invalid-feedback>
            CPF válido é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col md="6">
        <b-form-group label="Cargo do Representante Legal" label-class="required" label-for="legalRepresentativePosition">
          <b-form-input
            id="legalRepresentativePosition"
            :value="formData.legal_representative_position"
            placeholder="Digite o cargo do representante legal"
            :state="getFieldState('legal_representative_position')"
            :disabled="disabled"
            class="required"
            @input="updateField('legal_representative_position', $event)"
          />
          <b-form-invalid-feedback>
            Cargo do representante legal é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="E-mail do Representante Legal" label-class="required" label-for="legalRepresentativeEmail">
          <b-form-input
            id="legalRepresentativeEmail"
            :value="formData.legal_representative_email"
            type="email"
            placeholder="email@representante.com"
            :state="getFieldState('legal_representative_email')"
            :disabled="disabled"
            class="required"
            @input="updateField('legal_representative_email', $event)"
          />
          <b-form-invalid-feedback>
            E-mail válido do representante legal é obrigatório
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormRadioGroup,
  BFormInvalidFeedback
} from 'bootstrap-vue'
import { mask } from 'vue-the-mask'

export default {
  name: 'BasicDataStep',
  directives: {
    mask
  },
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormRadioGroup,
    BFormInvalidFeedback
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    primaryVariant: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:form-data'],
  data() {
    return {
      credentialingTypeOptions: [
        { text: 'Credenciamento', value: 'CR' },
        { text: 'Recredenciamento', value: 'RC' }
      ],
      activityAreaOptions: [
        { text: 'Saúde', value: 'HE' },
        { text: 'Educação', value: 'ED' },
        { text: 'Assistência Social', value: 'SA' }
      ]
    }
  },
  computed: {
    currentCredentialingType() {
      return this.formData.credentialing_type || null;
    },
    currentActivityArea() {
      return this.formData.activity_area || null;
    }
  },
  methods: {
    getFieldState(fieldName) {
      if (this.errors[fieldName]) {
        return false;
      }
      return null;
    },
    updateField(fieldName, value) {
      console.log(`updateField(${fieldName}):`, value); // Debug
      this.$emit('update:form-data', { [fieldName]: value });
    },
    getRadioGroupClass() {
      return !this.primaryVariant ? 'radio-group-dynamic-primary' : `radio-group-${this.primaryVariant}`;
    }
  },
  watch: {
    'formData.credentialing_type': {
      handler(newValue) {
        console.log('credentialing_type changed:', newValue);
      },
      immediate: true
    },
    'formData.activity_area': {
      handler(newValue) {
        console.log('activity_area changed:', newValue);
      },
      immediate: true
    }
  },
  mounted() {
    // Debug para verificar os valores iniciais
    console.log('BasicDataStep mounted:', {
      credentialing_type: this.formData.credentialing_type,
      activity_area: this.formData.activity_area
    });
  }
}
</script>

<style scoped>
.step-content {
  padding: 1rem 0;
}

.form-check {
  margin-bottom: 0.5rem;
}

h6.text-muted {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}

/* Estilo para campo readonly */
.form-control[readonly] {
  background-color: #f8f9fa !important;
  border-color: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}

.form-control[readonly]:focus {
  background-color: #f8f9fa !important;
  border-color: #e9ecef !important;
  box-shadow: none !important;
}

/* Texto explicativo */
.form-text.text-muted {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-text.text-muted .feather {
  font-size: 0.8rem;
  color: #6c757d;
}
</style>

<style>
/* Radio buttons com cores dinâmicas - estilos globais para alcançar componentes do Bootstrap Vue */
.radio-group-dynamic-primary .custom-control-input:checked ~ .custom-control-label::before,
.radio-group-dynamic-primary input[type="radio"]:checked {
  background-color: var(--dynamic-primary, #156440) !important;
  border-color: var(--dynamic-primary, #156440) !important;
}

.radio-group-dynamic-primary .custom-control-input:focus ~ .custom-control-label::before,
.radio-group-dynamic-primary input[type="radio"]:focus {
  box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
  border-color: var(--dynamic-primary, #156440) !important;
}

.radio-group-dynamic-primary .custom-control-input:focus:not(:checked) ~ .custom-control-label::before {
  border-color: var(--dynamic-primary, #156440) !important;
}

.radio-group-dynamic-primary .custom-control-input:not(:disabled):active ~ .custom-control-label::before {
  background-color: var(--dynamic-primary, #156440) !important;
  border-color: var(--dynamic-primary, #156440) !important;
}

/* Suporte específico para Bootstrap Vue radio groups */
.radio-group-dynamic-primary .form-check-input:checked {
  background-color: var(--dynamic-primary, #156440) !important;
  border-color: var(--dynamic-primary, #156440) !important;
}

.radio-group-dynamic-primary .form-check-input:focus {
  border-color: var(--dynamic-primary, #156440) !important;
  box-shadow: 0 0 0 0.25rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
}

/* Seletores mais específicos para b-form-radio-group */
.radio-group-dynamic-primary .form-check .form-check-input:checked {
  background-color: var(--dynamic-primary, #156440) !important;
  border-color: var(--dynamic-primary, #156440) !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='2' fill='%23fff'/%3e%3c/svg%3e") !important;
}

.radio-group-dynamic-primary .form-check .form-check-input:focus {
  border-color: var(--dynamic-primary, #156440) !important;
  outline: 0 !important;
  box-shadow: 0 0 0 0.25rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
}

/* Forçar cores dinâmicas com seletores muito específicos */
div.radio-group-dynamic-primary .bv-no-focus-ring .form-check .form-check-input:checked,
div.radio-group-dynamic-primary .form-check input[type="radio"]:checked,
.radio-group-dynamic-primary div .form-check-input:checked {
  background-color: var(--dynamic-primary, #156440) !important;
  border-color: var(--dynamic-primary, #156440) !important;
}

div.radio-group-dynamic-primary .bv-no-focus-ring .form-check .form-check-input:focus,
div.radio-group-dynamic-primary .form-check input[type="radio"]:focus,
.radio-group-dynamic-primary div .form-check-input:focus {
  border-color: var(--dynamic-primary, #156440) !important;
  box-shadow: 0 0 0 0.25rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
}

/* Sobrescrever cores primary padrão do Bootstrap Vue */
.radio-group-dynamic-primary .form-check-input {
  accent-color: var(--dynamic-primary, #156440) !important;
}

/* Estados de validação para radio buttons */
.bv-no-focus-ring.is-invalid .form-check-input,
.form-check-input.is-invalid {
  border-color: var(--bs-danger, #ea5455) !important;
}

.bv-no-focus-ring.is-invalid .form-check .form-check-input,
.is-invalid .form-check-input {
  border-color: var(--bs-danger, #ea5455) !important;
}

/* Radio group com estado inválido */
.bv-no-focus-ring.is-invalid .form-check,
.is-invalid .form-check {
  border-left: 3px solid var(--bs-danger, #ea5455);
  padding-left: 0.75rem;
  margin-left: -0.75rem;
  background-color: rgba(234, 84, 85, 0.1);
  border-radius: 0.25rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

/* Inputs com foco usando cores dinâmicas */
.step-content .form-control:focus {
  border-color: var(--dynamic-primary, #156440) !important;
  box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
}

.step-content .form-control.is-valid {
  border-color: var(--bs-success, #28c76f) !important;
}

.step-content .form-control.is-invalid {
  border-color: var(--bs-danger, #ea5455) !important;
}

/* Estilos para labels com classe required */
.required {
  /* Classe mantida para compatibilidade com validações */
}

/* Melhorar a compatibilidade com o CSS já existente do projeto para campos required */
.step-content .required.form-control,
.step-content .form-control.required {
  border-left: 3px solid #ff9f43 !important;
  transition: border-color 0.5s ease-out;
}

.step-content .required.form-control:focus,
.step-content .form-control.required:focus {
  border-left-color: var(--dynamic-primary, #156440) !important;
  border-color: var(--dynamic-primary, #156440) !important;
  box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
}
</style>
